import { useRequiredContext } from "@redotech/react-util/context";
import CheckIcon from "@redotech/redo-icons/check_filled.svg";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import FileIcon from "@redotech/redo-icons/file-06.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01_filled.svg";
import StarsIcon from "@redotech/redo-icons/stars-01.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { motion } from "motion/react";
import { memo, useState } from "react";

export interface KnowledgeSource {
  id: string;
  title: string;
}

export interface SuggestedReplyCardProps {
  suggestedText: string;
  onAccept: () => void;
  onDecline: () => void;
  onRefresh: () => void;
  loading: boolean;
  knowledgeSources?: KnowledgeSource[];
  knowledgeSourcesLoading?: boolean;
}

export const SuggestedReplyCard = memo(function SuggestedReplyCard({
  suggestedText,
  onAccept,
  onDecline,
  onRefresh,
  loading,
  knowledgeSources = [],
  knowledgeSourcesLoading = false,
}: SuggestedReplyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const routes = useRequiredContext(MerchantRoutesContext);

  const preview = suggestedText.replace(/\s+/g, " ").trim();
  const hasKnowledgeSources = knowledgeSources.length > 0;

  return (
    <motion.div
      animate={{ opacity: 1 }}
      data-testid="copilot-suggestion-card"
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        padding: "8px",
        margin: "12px 16px",
        width: "calc(100% - 32px)",
        backgroundColor: "var(--color-bg-primary)",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
      }}
      transition={{ duration: 0.15 }}
    >
      <Flex align="center" gap="sm" justify="space-between">
        <Flex align="center" gap="sm">
          <Flex
            align="center"
            justify="center"
            style={{
              width: 20,
              height: 20,
              borderRadius: "4px",
              backgroundColor: "var(--color-bg-tertiary)",
            }}
          >
            <StarsIcon
              style={{
                width: 12,
                height: 12,
                color: "var(--color-text-secondary)",
              }}
            />
          </Flex>
          <Text fontWeight="semibold" style={{ fontSize: "12px" }}>
            AI Suggested Reply
          </Text>
        </Flex>
        <Flex gap="sm">
          <RedoButton
            data-testid="copilot-refresh-button"
            disabled={loading}
            hierarchy="tertiary"
            IconLeading={RefreshIcon}
            onClick={onRefresh}
            size="sm"
          />
          <RedoButton
            data-testid="copilot-decline-button"
            disabled={loading}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onDecline}
            size="sm"
          />
          <RedoButton
            data-testid="copilot-accept-button"
            disabled={loading}
            hierarchy="primary"
            IconLeading={CheckIcon}
            onClick={onAccept}
            size="sm"
          />
        </Flex>
      </Flex>
      <Flex
        align="flex-start"
        gap="sm"
        onClick={() => setExpanded(!expanded)}
        style={{
          cursor: "pointer",
          padding: "6px",
          backgroundColor: "var(--color-bg-tertiary)",
          borderRadius: "6px",
          border: "1px solid var(--color-border-secondary)",
          maxHeight: expanded ? "400px" : "80px",
          overflow: "auto",
          transition: "max-height 0.2s ease",
        }}
      >
        <ChevronDownIcon
          style={{
            width: 11,
            height: 11,
            opacity: 0.5,
            marginTop: "4px",
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease",
          }}
        />
        <Text
          style={{
            whiteSpace: expanded ? "pre-wrap" : "nowrap",
            lineHeight: "1.5",
            fontSize: "12px",
            color: "var(--color-text-primary)",
            opacity: expanded ? 1 : 0.6,
            transition: "opacity 0.2s ease",
            flex: 1,
            overflow: expanded ? "visible" : "hidden",
            textOverflow: expanded ? "clip" : "ellipsis",
          }}
        >
          {expanded ? suggestedText : preview}
        </Text>
      </Flex>
      {(hasKnowledgeSources || knowledgeSourcesLoading) && (
        <Flex
          align="center"
          gap="xs"
          onClick={() => setSourcesExpanded(!sourcesExpanded)}
          style={{ cursor: "pointer", paddingTop: "4px" }}
        >
          <ChevronDownIcon
            style={{
              width: 10,
              height: 10,
              opacity: 0.5,
              transform: sourcesExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease",
            }}
          />
          <Text color="secondary" fontSize="xs">
            {knowledgeSourcesLoading
              ? "Loading sources..."
              : `Used ${knowledgeSources.length} source${knowledgeSources.length !== 1 ? "s" : ""}`}
          </Text>
        </Flex>
      )}
      {sourcesExpanded && hasKnowledgeSources && (
        <Flex dir="column" gap="xs" mt="sm" pl="xl">
          {knowledgeSources.map((source) => (
            <Flex
              align="center"
              as="a"
              gap="xs"
              key={source.id}
              linkProps={{
                to: routes.getSettingsSubRoute(
                  `ai/knowledge?article=${source.id}`,
                ),
                target: "_blank",
                rel: "noopener noreferrer",
              }}
              textColor="primary"
              textDecoration="none"
            >
              <RedoIcon color="inherit" Icon={FileIcon} size={12} />
              <Text
                fontSize="xs"
                minW="none"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {source.title}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </motion.div>
  );
});
